/**
 * Form logic for the AI token allowance — the counterpart to
 * `AiTokenLimitFields`, which only renders it.
 *
 * Kept separate from both components so Add Client, Edit Client and the JLL
 * usage dialog share one set of rules, and so the rules can be reasoned about
 * (and tested) without mounting React. Mirrors the server-side validation in
 * `survey-analytics.service/shared/validation.py`.
 */

import { DEFAULT_ALERT_THRESHOLD_PERCENT } from '../../constants/aiUsage';

/** Matches MAX_TOKEN_LIMIT in shared/validation.py. */
export const MAX_TOKEN_LIMIT = 1e15;

/** Blank state for Add Client — no allowance is set until the user types one. */
export const emptyTokenLimitValues = () => ({
  inputTokenLimit: '',
  outputTokenLimit: '',
  alertThresholdPercent: String(DEFAULT_ALERT_THRESHOLD_PERCENT),
  isEnforced: false,
});

/**
 * Map an `aiTokenLimit` payload from the API onto form values.
 * An unconfigured client comes back with `isConfigured: false`, which must show
 * as empty fields rather than a misleading "0".
 */
export const tokenLimitValuesFromApi = (limit) => {
  if (!limit || !limit.isConfigured) {
    return emptyTokenLimitValues();
  }
  return {
    inputTokenLimit: String(limit.inputTokenLimit ?? ''),
    outputTokenLimit: String(limit.outputTokenLimit ?? ''),
    alertThresholdPercent: String(limit.alertThresholdPercent ?? DEFAULT_ALERT_THRESHOLD_PERCENT),
    isEnforced: !!limit.isEnforced,
  };
};

const isBlank = (value) => value === '' || value === null || value === undefined;

/** True when the user has entered either limit — i.e. intends to set an allowance. */
export const hasTokenLimitInput = (values) =>
  !isBlank(values?.inputTokenLimit) || !isBlank(values?.outputTokenLimit);

/**
 * Validate the allowance fields.
 * @param {object} values form values
 * @param {{ required?: boolean }} [options] `required` forces both limits
 *   (the JLL dialog, where saving an allowance is the whole point)
 * @returns {object} field name -> message; empty when valid
 */
export const validateTokenLimitValues = (values, { required = false } = {}) => {
  const errors = {};
  const touched = hasTokenLimitInput(values);

  if (!required && !touched) {
    // Allowance left untouched — nothing to validate or send
    return errors;
  }

  ['inputTokenLimit', 'outputTokenLimit'].forEach((field) => {
    const raw = values?.[field];
    const label = field === 'inputTokenLimit' ? 'Input token limit' : 'Output token limit';

    if (isBlank(raw)) {
      errors[field] = `${label} is required`;
      return;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
      errors[field] = `${label} must be a whole number`;
    } else if (parsed < 0) {
      errors[field] = `${label} cannot be negative`;
    } else if (parsed > MAX_TOKEN_LIMIT) {
      errors[field] = `${label} is too large`;
    }
  });

  const threshold = values?.alertThresholdPercent;
  if (!isBlank(threshold)) {
    const parsed = Number(threshold);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 1 || parsed > 100) {
      errors.alertThresholdPercent = 'Alert threshold must be between 1 and 100';
    }
  }

  return errors;
};

/**
 * The `aiTokenLimit` object to send, or `null` when the user set no allowance.
 * Callers omit the key entirely when this returns `null`, so a client can be
 * created or renamed without touching its budget.
 */
export const buildTokenLimitPayload = (values) => {
  if (!hasTokenLimitInput(values)) {
    return null;
  }
  return {
    inputTokenLimit: Number(values.inputTokenLimit),
    outputTokenLimit: Number(values.outputTokenLimit),
    alertThresholdPercent: isBlank(values.alertThresholdPercent)
      ? DEFAULT_ALERT_THRESHOLD_PERCENT
      : Number(values.alertThresholdPercent),
    isEnforced: !!values.isEnforced,
  };
};
